/** RBAC permission definitions */
export const ROLES = ['admin', 'editor', 'viewer', 'agent'] as const
export type Role = (typeof ROLES)[number]

export const PERMISSIONS: Record<Role, string[]> = {
  admin: ['tenant:manage', 'user:manage', 'doc:*', 'storage:*', 'site:*', 'folder:*', 'key:*', 'secret:*', 'audit:read'],
  editor: [
    'doc:create', 'doc:read', 'doc:update', 'doc:delete', 'doc:search', 'doc:share',
    'storage:read', 'storage:upload', 'storage:delete',
    'site:read', 'site:upload', 'site:update', 'site:delete', 'site:rollback', 'site:summary',
    'folder:read', 'folder:create', 'folder:update', 'folder:delete',
    'secret:read', 'secret:create', 'secret:update', 'secret:import', 'secret:export',
  ],
  viewer: ['doc:read', 'doc:search', 'storage:read', 'site:read', 'folder:read'],
  agent: ['doc:read', 'doc:search'],
}

/** All valid API key scopes */
export const VALID_SCOPES = [
  'doc:read', 'doc:create', 'doc:update', 'doc:delete', 'doc:search', 'doc:share',
  'storage:read', 'storage:upload', 'storage:delete',
  'site:read', 'site:upload', 'site:update', 'site:delete', 'site:rollback', 'site:summary',
  'folder:read', 'folder:create', 'folder:update', 'folder:delete',
  'key:read', 'key:create', 'key:update', 'key:revoke',
  'secret:read', 'secret:create', 'secret:update', 'secret:delete', 'secret:destroy',
  'secret:reveal', 'secret:inject', 'secret:grant', 'secret:audit',
  'secret:import', 'secret:export',
] as const

/** Wildcard scopes — grants all actions for a resource */
export const WILDCARD_SCOPES = ['doc:*', 'storage:*', 'site:*', 'folder:*', 'key:*', 'secret:*', '*:*'] as const

/** All accepted scopes (valid + wildcards) */
export const ALL_SCOPES = [...VALID_SCOPES, ...WILDCARD_SCOPES] as const

/** Scope categories for UI grouping */
export const SCOPE_CATEGORIES = {
  doc: { label: 'Documents', scopes: ['doc:read', 'doc:create', 'doc:update', 'doc:delete', 'doc:search', 'doc:share'] },
  storage: { label: 'Storage', scopes: ['storage:read', 'storage:upload', 'storage:delete'] },
  site: { label: 'Static Sites', scopes: ['site:read', 'site:upload', 'site:update', 'site:delete', 'site:rollback', 'site:summary'] },
  folder: { label: 'Folders', scopes: ['folder:read', 'folder:create', 'folder:update', 'folder:delete'] },
  key: { label: 'API Keys', scopes: ['key:read', 'key:create', 'key:update', 'key:revoke'] },
  secret: { label: 'Secrets', scopes: ['secret:read', 'secret:create', 'secret:update', 'secret:delete', 'secret:destroy', 'secret:reveal', 'secret:inject', 'secret:grant', 'secret:audit', 'secret:import', 'secret:export'] },
} as const

/** Rate limit configs */
export const RATE_LIMITS = {
  api: { limit: 100, windowSec: 60 },
  search: { limit: 50, windowSec: 60 },
  searchExpand: { limit: 10, windowSec: 60 }, // stricter limit when expand=true (AI cost surface)
  suggest: { limit: 100, windowSec: 60 },
  web: { limit: 1000, windowSec: 60 },
} as const

/** Access levels for documents */
export const ACCESS_LEVELS = ['private', 'specific', 'public'] as const
export type AccessLevel = (typeof ACCESS_LEVELS)[number]

/** Auth token TTLs */
export const TOKEN_TTL = {
  accessToken: 60 * 60 * 1000, // 1 hour
  refreshToken: 30 * 24 * 60 * 60 * 1000, // 30 days
  shareLink: 90 * 24 * 60 * 60 * 1000, // 90 days
} as const

/** API key prefix */
export const API_KEY_PREFIX = 'aw_'

/** Supported AI providers with available models */
export const AI_PROVIDERS = {
  openai: { name: 'OpenAI', models: ['gpt-4o', 'gpt-4o-mini', 'o3-mini', 'gpt-4-turbo'] },
  anthropic: { name: 'Anthropic', models: ['claude-sonnet-4-6', 'claude-haiku-4-5-20251001', 'claude-opus-4-6'] },
  google: { name: 'Google Gemini', models: ['gemini-2.5-flash', 'gemini-2.5-flash-lite', 'gemini-2.5-pro', 'gemini-2.0-flash', 'gemini-3-flash-preview', 'gemini-3.1-pro-preview', 'gemini-3.1-flash-lite-preview'] },
  openrouter: { name: 'OpenRouter', models: ['auto'] },
  minimax: { name: 'MiniMax', models: ['MiniMax-M1', 'MiniMax-T1'] },
  alibaba: { name: 'Alibaba', models: ['qwen-turbo', 'qwen-plus', 'qwen-max'] },
} as const

/** AI rate limit: requests per user per minute */
export const AI_RATE_LIMIT = { maxRequests: 15, intervalMs: 60_000 } as const

/** Graph AI extraction credit policy: only Cloudflare-hosted extraction deducts AgentWiki credits. */
export const GRAPH_EXTRACTION_CREDIT_COST = 5

/** Cloudflare Workers AI models exposed for graph extraction. */
export const CLOUDFLARE_GRAPH_MODELS = [
  { id: '@cf/zai-org/glm-5.2', name: 'GLM-5.2', contextTokens: 262_144 },
  { id: '@cf/moonshotai/kimi-k2.7-code', name: 'Kimi K2.7 Code', contextTokens: 262_100 },
  { id: '@cf/google/gemma-4-26b-a4b-it', name: 'Gemma 4 26B A4B Instruct', contextTokens: 256_000 },
  { id: '@cf/zai-org/glm-4.7-flash', name: 'GLM-4.7 Flash', contextTokens: 131_072 },
] as const

export const DEFAULT_CLOUDFLARE_GRAPH_MODEL = CLOUDFLARE_GRAPH_MODELS[0].id
